import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '../../../hooks/useAuth'
import { buscarDetalleJobCargaMasiva } from '../../servicios/publicacionesService'
import BordeDetalle from '../../ui/BordeDetalle'
import styles from './CargaMasivaDetalle.module.css'

const ESTADOS_LABEL = {
  pending: 'Pendiente',
  processing: 'Procesando',
  completed: 'Completada',
  completed_with_errors: 'Completada con errores',
  failed: 'Fallida'
}

const ESTADOS_CLASE = {
  pending: styles.chipNeutro,
  processing: styles.chipInfo,
  completed: styles.chipExito,
  completed_with_errors: styles.chipAlerta,
  failed: styles.chipError
}

const CargaMasivaDetalle = () => {
  const [job, setJob] = useState(null)
  const [noEncontrado, setNoEncontrado] = useState(false)
  const navigate = useNavigate()
  const { jobId } = useParams()
  const { loginDataContext: { idUsuario } = {} } = useAuth()

  const obtenerDetalle = async () => {
    try {
      const { data } = await buscarDetalleJobCargaMasiva(jobId)
      setJob(data.body)
    } catch {
      setNoEncontrado(true)
    }
  }

  useEffect(() => {
    if (!idUsuario) {
      return
    }
    obtenerDetalle()
  }, [jobId, idUsuario])

  const onCerrarClick = () => {
    navigate('/Publicaciones/Administrar')
  }

  useEffect(() => {
    if (noEncontrado) {
      onCerrarClick()
    }
  }, [noEncontrado])

  if (noEncontrado) {
    return (
      <BordeDetalle titulo="Detalle de carga masiva" onCerrarClick={onCerrarClick}>
        <div className={styles.contenedorGeneral}>
          <p>No se encontró el job de carga masiva solicitado.</p>
        </div>
      </BordeDetalle>
    )
  }

  if (!job) {
    return (
      <BordeDetalle titulo="Detalle de carga masiva" onCerrarClick={onCerrarClick}>
        <div className={styles.contenedorGeneral}>
          <p>Cargando...</p>
        </div>
      </BordeDetalle>
    )
  }

  const publicacionesCreadas = job.publicaciones?.length ?? 0

  return (
    <BordeDetalle titulo="Detalle de carga masiva" onCerrarClick={onCerrarClick}>
      <div className={styles.contenedorGeneral}>
        <div className={styles.headerDetalle}>
          <div className={styles.columnaLeft}>
            <span className={styles.nombrePublicacion}>Job #{job.id}</span>
            <span className={styles.subtitulo}>{job.fileName || 'Sin nombre de archivo'}</span>
          </div>
          <div className={styles.columnaAuto}>
            <span className={`${styles.chip} ${ESTADOS_CLASE[job.status] || styles.chipNeutro}`}>
              {ESTADOS_LABEL[job.status] || job.status}
            </span>
            {job.finishedAt && (
              <span className={styles.fechaFinalizado}>
                Finalizado el {dayjs(job.finishedAt).format('DD/MM/YYYY HH:mm')}
              </span>
            )}
          </div>
        </div>

        <div className={styles.flexRow}>
          <div className={styles.metrica}>
            <span className={styles.metricaValor}>{job.totalRows ?? 0}</span>
            <span className={styles.metricaLabel}>Publicaciones</span>
          </div>
          <div className={styles.metrica}>
            <span className={`${styles.metricaValor} ${styles.metricaExito}`}>
              {publicacionesCreadas}
            </span>
            <span className={styles.metricaLabel}>Publicaciones creadas</span>
          </div>
          <div className={styles.metrica}>
            <span
              className={`${styles.metricaValor} ${job.errores?.length ? styles.metricaError : ''}`}
            >
              {job.errores?.length ?? 0}
            </span>
            <span className={styles.metricaLabel}>Errores</span>
          </div>
        </div>

        {job.errorMessage && (
          <div className={styles.errorGeneral}>
            <strong>Error del job:</strong> {job.errorMessage}
          </div>
        )}

        {job.publicaciones?.length > 0 && (
          <>
            <h3 className={styles.tituloSeccion}>Publicaciones creadas</h3>
            <div className={styles.tablaWrapper}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th>Nro.</th>
                    <th>ID</th>
                    <th>Publicación</th>
                  </tr>
                </thead>
                <tbody>
                  {job.publicaciones.map((publicacion, index) => (
                    <tr
                      key={publicacion.idPublicacion}
                      className={styles.filaClickeable}
                      onClick={() => navigate(`/Publicacion/${publicacion.idPublicacion}`)}
                    >
                      <td>{index + 1}</td>
                      <td>{publicacion.idPublicacion}</td>
                      <td className={styles.celdaLink}>{publicacion.titulo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <h3 className={styles.tituloSeccion}>Errores</h3>
        {job.errores?.length > 0 ? (
          <div className={styles.tablaWrapper}>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th>Nro.</th>
                  <th>Categoría</th>
                  <th>Publicación</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {job.errores.map((error, index) => (
                  <tr key={`${error.rowNumber}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{error.nombreHoja || '-'}</td>
                    <td>{error.nombrePublicacion || '-'}</td>
                    <td className={styles.celdaError}>{error.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={styles.sinErrores}>No se registraron errores en este job.</p>
        )}
      </div>
    </BordeDetalle>
  )
}

export default CargaMasivaDetalle
